import { useState } from 'react';
import {
  Check,
  Eye,
  Globe,
  History,
  Pencil,
  PowerOff,
  RotateCcw,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import type { GuidanceDto, JourneyStepDto } from '@ederest/contracts';
import { ApiError } from '../api/client.js';
import type { GuidanceActions } from '../api/queries.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { BilingualPreview } from './BilingualPreview.js';
import { DismissDialog } from './DismissDialog.js';
import { GuidanceEditor } from './GuidanceEditor.js';
import { GuidanceHistory } from './GuidanceHistory.js';
import {
  COMPONENT_LABEL,
  DISMISSAL_REASON_LABEL,
  STATE_LABEL,
  describeTrigger,
  isRtl,
  missingLanguages,
} from './labels.js';
import './guidance.css';

/**
 * Domain errors, said in the language of the team that reads them. The domain
 * speaks English by repository rule; a business team of a ministry does not, and
 * "Component \"help_bubble\" requires at least one selector" stops an agent who
 * would otherwise have to call a developer — which the phase 5 criterion
 * forbids.
 */
const DOMAIN_ERROR_LABEL: Readonly<Record<string, string>> = {
  MISSING_ANCHOR:
    'Ce composant doit désigner un élément de la page. Choisissez un « Élément ciblé » plus haut dans le formulaire, parmi ceux que la mesure a observés sur cette étape.',
  REINFORCED_REQUIRES_GUIDED_TOUR:
    'Le guidage renforcé est une option du parcours guidé seulement. Changez de composant ou désactivez-le.',
};

interface GuidanceCardProps {
  guidance: GuidanceDto;
  languages: readonly string[];
  steps: readonly JourneyStepDto[];
  actions: GuidanceActions;
  /** The recommendation flagged the proposal as written without the model (functional §5.6). */
  contentGenerationSkipped?: boolean;
  dismissalReason?: string | null;
}

/**
 * A guidance produced by the engine, with every decision of functional §8.4:
 * validate, adjust, preview in both languages, publish, disable, delete,
 * dismiss with a reason, and read the change log. Shared by the journey tab
 * and the recommendation queue: one editing surface, one write path.
 */
export function GuidanceCard({
  guidance,
  languages,
  steps,
  actions,
  contentGenerationSkipped,
  dismissalReason,
}: GuidanceCardProps) {
  const [language, setLanguage] = useState(languages[0] ?? 'fr');
  const [mode, setMode] = useState<'read' | 'edit' | 'preview' | 'history'>('read');
  const [dismissing, setDismissing] = useState(false);
  const content = guidance.contents[language] ?? { title: '', body: '', items: [] };
  const state = STATE_LABEL[guidance.state];
  const missing = missingLanguages(guidance, languages);
  const versioned = { id: guidance.id, rowVersion: guidance.rowVersion };

  const pending = Object.values(actions).find((mutation) => mutation.isPending);
  const busy = Boolean(pending);
  const failure = Object.values(actions).find((mutation) => mutation.isError)?.error;
  const conflict = failure instanceof ApiError && (failure.status === 412 || failure.status === 428);

  return (
    <article className="guidance" data-testid="guidance-card" data-state={guidance.state}>
      <header className="guidance__head">
        <span className="guidance__component">
          <Sparkles size={13} />
          {COMPONENT_LABEL[guidance.component]}
        </span>
        <span className="guidance__badges">
          {contentGenerationSkipped && missing.length > 0 && <Badge tone="warning">Sans texte rédigé</Badge>}
          {guidance.currentVersion > 0 && <Badge tone="neutral">v{guidance.currentVersion}</Badge>}
          <Badge tone={state.tone}>{state.text}</Badge>
        </span>
      </header>

      {mode === 'edit' ? (
        <GuidanceEditor
          guidance={guidance}
          languages={languages}
          steps={steps}
          busy={busy}
          onCancel={() => setMode('read')}
          onSave={(patch) =>
            actions.update.mutate({ ...versioned, patch }, { onSuccess: () => setMode('read') })
          }
        />
      ) : mode === 'preview' ? (
        <BilingualPreview guidance={guidance} languages={languages} />
      ) : mode === 'history' ? (
        <GuidanceHistory guidanceId={guidance.id} />
      ) : (
        <>
          <div className="guidance__langs" role="tablist" aria-label="Langue du contenu">
            {languages.map((code) => (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={code === language}
                className={`guidance__lang${code === language ? ' guidance__lang--active' : ''}`}
                onClick={() => setLanguage(code)}
              >
                <Globe size={11} />
                {code.toUpperCase()}
                {missing.includes(code) && (
                  <span className="guidance__lang-dot" aria-label="contenu manquant" />
                )}
              </button>
            ))}
          </div>
          <div className="guidance__preview" dir={isRtl(language) ? 'rtl' : 'ltr'}>
            <p className="guidance__title">{content.title || <em>Titre à rédiger</em>}</p>
            <p className="guidance__body">{content.body || <em>Contenu à rédiger depuis ce panneau</em>}</p>
            {content.items.length > 0 && (
              <ul className="guidance__items">
                {content.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
          <p className="guidance__trigger">
            Déclencheur : <strong>{describeTrigger(guidance.trigger)}</strong>
            {` · ${guidance.trigger.maxDisplaysPerSession} affichage${guidance.trigger.maxDisplaysPerSession > 1 ? 's' : ''} par session`}
            {guidance.dismissalMemory === 'none' && ' · réapparaît après fermeture'}
            {guidance.reinforced && ' · guidage renforcé'}
          </p>
        </>
      )}

      {mode !== 'edit' && missing.length > 0 && guidance.state !== 'dismissed' && (
        <p className="guidance__warning" data-testid="publication-blocked">
          Publication bloquée : contenu manquant en {missing.map((c) => c.toUpperCase()).join(', ')}.
        </p>
      )}

      {guidance.state === 'dismissed' && dismissalReason && (
        <p className="guidance__note">
          Écartée :{' '}
          {DISMISSAL_REASON_LABEL[dismissalReason as keyof typeof DISMISSAL_REASON_LABEL] ?? dismissalReason}
        </p>
      )}

      {failure instanceof ApiError && (
        <p className="guidance__warning" role="alert">
          {conflict
            ? 'Cette guidance a été modifiée entre-temps. Rechargez la page avant de décider.'
            : (DOMAIN_ERROR_LABEL[failure.code] ?? failure.message)}
        </p>
      )}

      {mode !== 'edit' && (
        <footer className="guidance__actions">
          <span className="guidance__secondary">
            <Button
              variant="ghost"
              icon={<Eye />}
              aria-pressed={mode === 'preview'}
              onClick={() => setMode(mode === 'preview' ? 'read' : 'preview')}
            >
              Prévisualiser
            </Button>
            <Button
              variant="ghost"
              icon={<History />}
              aria-pressed={mode === 'history'}
              onClick={() => setMode(mode === 'history' ? 'read' : 'history')}
            >
              Journal
            </Button>
            {guidance.state !== 'dismissed' && (
              <Button variant="ghost" icon={<Pencil />} onClick={() => setMode('edit')} disabled={busy}>
                Modifier
              </Button>
            )}
          </span>
          <span className="guidance__primary">
            {guidance.state === 'proposed' && (
              <>
                <Button
                  variant="ghost"
                  icon={<XCircle />}
                  onClick={() => setDismissing(true)}
                  disabled={busy}
                >
                  Écarter
                </Button>
                <Button
                  variant="primary"
                  icon={<Check />}
                  onClick={() => actions.approve.mutate(versioned)}
                  disabled={busy}
                >
                  Valider
                </Button>
              </>
            )}
            {guidance.state === 'approved' && (
              <>
                <Button
                  variant="ghost"
                  icon={<RotateCcw />}
                  onClick={() => actions.revert.mutate(versioned)}
                  disabled={busy}
                >
                  Renvoyer en proposition
                </Button>
                <Button
                  variant="ghost"
                  icon={<Trash2 />}
                  onClick={() => actions.remove.mutate(versioned)}
                  disabled={busy}
                >
                  Supprimer
                </Button>
                <Button
                  variant="primary"
                  icon={<Check />}
                  onClick={() => actions.publish.mutate(versioned)}
                  disabled={busy || missing.length > 0}
                >
                  Publier
                </Button>
              </>
            )}
            {guidance.state === 'published' && (
              <>
                <Button
                  variant="ghost"
                  icon={<Trash2 />}
                  onClick={() => actions.remove.mutate(versioned)}
                  disabled={busy}
                >
                  Supprimer
                </Button>
                <Button
                  variant="danger"
                  icon={<PowerOff />}
                  onClick={() => actions.disable.mutate(versioned)}
                  disabled={busy}
                >
                  Désactiver
                </Button>
              </>
            )}
            {guidance.state === 'disabled' && (
              <>
                <Button
                  variant="ghost"
                  icon={<Trash2 />}
                  onClick={() => actions.remove.mutate(versioned)}
                  disabled={busy}
                >
                  Supprimer
                </Button>
                <Button
                  variant="primary"
                  icon={<Check />}
                  onClick={() => actions.publish.mutate(versioned)}
                  disabled={busy || missing.length > 0}
                >
                  Republier
                </Button>
              </>
            )}
          </span>
        </footer>
      )}

      {dismissing && (
        <DismissDialog
          title="Écarter cette proposition"
          busy={busy}
          onCancel={() => setDismissing(false)}
          onConfirm={(reason, note) =>
            actions.dismiss.mutate({ ...versioned, reason, note }, { onSettled: () => setDismissing(false) })
          }
        />
      )}
    </article>
  );
}
